import PdfPreview from '@/components/PdfPreview';
import { defaultApiErrorHandler } from '@/helpers/string';
import httpClient, { ApiSdk } from '@/httpclient';
import { getFileName } from '@/services/fileService';
import { DownloadOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, message, Modal, Row, Space, Spin } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import SignatureCanvas from 'react-signature-canvas';
import fileSaver from 'file-saver';

type Props = {
  onClose: () => void;
  onConfirm: (signature: string) => void;
  formValueRef: React.MutableRefObject<any>;
};

export default function SignContractModal({ onClose, onConfirm, formValueRef }: Props) {
  const [loading, setIsLoading] = React.useState(false);
  const { t } = useTranslation();
  const signatureRef = React.useRef<any>();
  const [signed, setIsSigned] = React.useState(false);
  const [checked1, setChecked1] = React.useState(false);
  const [checked2, setChecked2] = React.useState(false);
  const [file, setFile] = React.useState<{ fileUrl: string; fileName: string }>();

  React.useEffect(() => {
    if (window.innerWidth >= 1200) {
      setIsLoading(true);
      setFile(undefined);
      httpClient
        .post('/tours/generate-guide-contract', formValueRef.current, {
          responseType: 'blob',
        })
        .then(res => {
          const url = window.URL.createObjectURL(res.data);
          setFile({
            fileUrl: url,
            fileName: getFileName(res.headers),
          });
        })
        .catch(err => {
          defaultApiErrorHandler(err);
          onClose();
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, []);
  const onOk = () => {
    onConfirm(signatureRef.current.toDataURL('image/png'));
  };
  const downloadWidthUrl = (fileUrl: string, fileName: string) => {
    if (fileUrl) {
      fileSaver.saveAs(fileUrl, fileName);
    }
  };
  const download = () => {
    if (file?.fileUrl) {
      downloadWidthUrl(file.fileUrl, file.fileName || '');
    } else {
      const loader = message.loading('ダウンロード', 10000000);

      httpClient
        .post('/tours/generate-guide-contract', formValueRef.current, {
          responseType: 'blob',
        })
        .then(res => {
          const url = window.URL.createObjectURL(res.data);
          downloadWidthUrl(url, getFileName(res.headers));
        })
        .catch(err => {
          defaultApiErrorHandler(err);
        })
        .finally(() => {
          if (loader) {
            loader();
          }
        });
    }
  };
  return (
    <Modal
      className="nta-custom-form create-tour-form tour-info-modal"
      visible={true}
      onCancel={() => onClose()}
      style={{ top: 0, width: '100%', height: '100%' }}
      width={'100%'}
      title={t('guideContract_SignContractModalTitle')}
      okButtonProps={{ style: { display: 'none' } }}
      cancelButtonProps={{ style: { float: 'left', display: 'none' }, loading }}
      onOk={onOk}
      maskClosable={false}
    >
      <Spin spinning={loading}>
        <Row gutter={6}>
          {window.innerWidth > 1200 ? (
            <Col xs={24} md={24} xl={24} xxl={16} style={{ overflow: 'auto', display: 'flex', justifyContent: 'end' }}>
              {file?.fileUrl ? <PdfPreview url={file?.fileUrl} width={1190} /> : null}
            </Col>
          ) : (
            <Col xs={24} md={24} lg={8} xl={8}>
              <div style={{ margin: '20px auto', textAlign: 'center', padding: 30, backgroundColor: '#cccccc80' }}>
                <Button type="primary" icon={<DownloadOutlined />} onClick={download}>
                  ダウンロード
                </Button>
              </div>
            </Col>
          )}
          <Col xs={24} md={24} lg={16} xl={16} xxl={8}>
            <div style={{ marginTop: 40 }}>
              <Checkbox style={{ transform: 'scale(1.5)', transformOrigin: 0 }} checked={checked1} onClick={() => setChecked1(x => !x)}>
                「発注書兼請書」
              </Checkbox>
            </div>
            <div style={{ marginTop: 10 }}>
              <Checkbox style={{ transform: 'scale(1.5)', transformOrigin: 0 }} checked={checked2} onClick={() => setChecked2(x => !x)}>
                「確認、遵守事項」
              </Checkbox>
            </div>
            <div style={{ paddingLeft: 50, marginTop: 5 }}>(内容は発注書兼請書に掲載しているQRコードよりご確認ください）</div>
            <div>
              <div style={{ paddingLeft: 50, marginTop: 10 }}>を確認し、了承しました。</div>
              <div style={{ paddingLeft: 50 }}>下記欄にフルネームで署名してください。</div>
            </div>
            <div>
              <div style={{ textAlign: 'right', width: 400, maxWidth: 'calc(100vw - 50px)' }}>
                <Button
                  danger
                  size="small"
                  onClick={() => {
                    signatureRef.current?.clear();
                    setIsSigned(false);
                  }}
                  type="link"
                >
                  {t('zTable.reset')}
                </Button>
              </div>

              <div>
                <SignatureCanvas
                  onBegin={() => {
                    setIsSigned(true);
                  }}
                  ref={signatureRef}
                  penColor="black"
                  canvasProps={{
                    width: 400,
                    height: 200,
                    style: {
                      border: '2px solid #ccc',
                      maxWidth: '100%',
                    },
                  }}
                />
              </div>
            </div>
            <div></div>
            <div style={{ marginTop: 10, maxWidth: 400 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  onClick={onOk}
                  type="primary"
                  icon={<SaveOutlined />}
                  className="save-btn"
                  disabled={loading || !checked1 || !checked2 || !signed}
                >
                  引き受け
                </Button>
                <Button onClick={onClose} type="default">
                  {t('Close')}
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </Spin>
    </Modal>
  );
}
